/* Product-image palette. No external service or API key is used. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;
  const started = new WeakSet();
  const CACHE = 'nn-product-palette-v2';
  function apply(el, color, source) {
    if (!el.isConnected) return;
    const p = api.palette(color);
    const target = document.body;
    for (const [name, value] of Object.entries(p)) target.style.setProperty(name, value);
    el.dataset.paletteStatus = source;
    el.dataset.paletteColor = p['--nn-accent'];
  }
  async function imagePixels(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 7000);
    let bitmap;
    try {
      const response = await fetch(url, { credentials: 'omit', mode: 'cors', signal: controller.signal });
      if (!response.ok) throw new Error('Image unavailable');
      const blob = await response.blob();
      bitmap = await createImageBitmap(blob);
      const canvas = document.createElement('canvas');
      canvas.width = 64; canvas.height = 64;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(bitmap, 0, 0, 64, 64);
      return context.getImageData(0, 0, 64, 64).data;
    } finally { clearTimeout(timer); bitmap?.close(); }
  }
  async function start(el) {
    if (started.has(el)) return;
    started.add(el);
    const override = el.dataset.paletteOverride;
    if (/^#[0-9a-f]{6}$/i.test(override || '')) { apply(el, override, 'manual'); return; }
    const fallback = el.dataset.paletteFallback || '#45484c';
    if (el.dataset.paletteAuto !== 'true') { apply(el, fallback, 'theme'); return; }
    const src = el.dataset.paletteImage;
    if (!src) { apply(el, '#45484c', 'no-image'); return; }
    const key = el.dataset.productId + ':' + src;
    let cache = {};
    try { cache = JSON.parse(localStorage.getItem(CACHE) || '{}'); } catch (_) {}
    if (cache && typeof cache === 'object' && /^#[0-9a-f]{6}$/i.test(cache[key] || '')) {
      apply(el, cache[key], 'image-cached'); return;
    }
    try {
      const color = api.extract(await imagePixels(src), 64, 64);
      apply(el, color, 'image');
      try {
        cache = cache && typeof cache === 'object' && !Array.isArray(cache) ? cache : {};
        cache[key] = color;
        const entries = Object.entries(cache).slice(-40);
        localStorage.setItem(CACHE, JSON.stringify(Object.fromEntries(entries)));
      } catch (_) {}
    } catch (_) { apply(el, fallback, 'fallback'); }
  }
  function boot() { document.querySelectorAll('nn-reference-product[data-palette-image]').forEach(start); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
  document.addEventListener('shopify:section:load', boot);
})(typeof globalThis === 'undefined' ? this : globalThis, function () {
  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
  function rgb(hex) { return [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)); }
  function hex(values) { return '#' + values.map(v => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join(''); }
  function luminance(values) {
    const c = values.map(n => { n /= 255; return n <= .04045 ? n / 12.92 : Math.pow((n + .055) / 1.055, 2.4); });
    return c[0] * .2126 + c[1] * .7152 + c[2] * .0722;
  }
  function contrastWhite(color) { return 1.05 / (luminance(rgb(color)) + .05); }
  function readable(color) {
    let values = rgb(/^#[0-9a-f]{6}$/i.test(color || '') ? color : '#45484c');
    for (let n = 0; n < 50 && contrastWhite(hex(values)) < 4.5; n++) values = values.map(v => v * .96);
    return hex(values);
  }
  function mix(values, amount) { return hex(values.map(v => v * amount + 255 * (1 - amount))); }
  function palette(color) {
    const accent = readable(color), values = rgb(accent);
    return {
      '--nn-accent': accent, '--nn-accent-rgb': values.join(','),
      '--nn-pale': mix(values, .055), '--nn-tint': mix(values, .085),
      '--nn-soft': mix(values, .13), '--nn-accent-line': mix(values, .28),
      '--nn-accent-dark': hex(values.map(v => v * .76))
    };
  }
  function extract(data, width, height) {
    const colors = new Map(), grays = new Map();
    let total = 0, chromatic = 0;
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (data[i + 3] < 220) continue;
      const c = [data[i], data[i + 1], data[i + 2]], max = Math.max(...c), min = Math.min(...c);
      const light = (max + min) / 510;
      if (light > .92 || light < .035) continue;
      const sat = max ? (max - min) / max : 0;
      const nx = (x + .5) / width - .5, ny = (y + .5) / height - .5;
      const weight = .6 + Math.max(0, 1 - Math.hypot(nx, ny) * 1.6);
      total += weight;
      const colored = sat > .32 && max - min > 55 && light < .82;
      const bins = colored ? colors : grays;
      const key = colored ? c.map(v => Math.round(v / 32)).join(',') : Math.round(light * 12);
      const w = weight * (colored ? .25 + sat * sat * 3 : 1);
      const bin = bins.get(key) || { weight: 0, sum: [0, 0, 0] };
      bin.weight += w; c.forEach((v, j) => bin.sum[j] += v * w); bins.set(key, bin);
      if (colored) chromatic += weight;
    }
    const bins = colors.size && chromatic / Math.max(total, 1) > .06 ? colors : grays;
    if (!bins.size) return '#45484c';
    const winner = [...bins.values()].sort((a, b) => b.weight - a.weight)[0];
    return readable(hex(winner.sum.map(v => v / winner.weight)));
  }
  return { extract, palette, contrastWhite, readable };
});
